/**
 * TranscriptPanel
 *
 * Inline panel (not a floating overlay) that sits in the transcript column
 * beside the assessment section. Speaker name resolved from Speaker table.
 * Supports per-utterance speaker reassignment for correcting diarization errors.
 */

import React, { useState, useEffect, useCallback } from 'react';
import styles from './TranscriptDrawer.module.css';
import { scopedFetch } from '../../services/scoped-fetch';

const SPEAKER_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899'];

const TranscriptPanel = ({ deviceId, sessionId, speakerVersion, onClose, readOnly = false }) => {
    const [transcripts, setTranscripts] = useState([]);
    const [speakers, setSpeakers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [reassigning, setReassigning] = useState(null); // transcript id being reassigned

    const fetchTranscripts = useCallback(() => {
        if (!deviceId) return;
        setLoading(true);
        setError(null);
        // scopedFetch attaches X-Processing-Key (recorder) or X-Viewer-Token
        // (viewer) automatically when present. For TA dashboard usage the
        // cookie carries the auth and the request still works.
        scopedFetch(`/api/v1/devices/${deviceId}/transcripts/client`)
            .then(res => {
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                return res.json();
            })
            .then(data => {
                setTranscripts(
                    Array.isArray(data) ? data.sort((a, b) => a.start_time - b.start_time) : []
                );
                setLoading(false);
            })
            .catch(err => {
                setError(err.message);
                setLoading(false);
            });
    }, [deviceId]);

    // Refetch when device changes or when speakers are renamed (speakerVersion bumps)
    useEffect(() => { fetchTranscripts(); }, [fetchTranscripts, speakerVersion]);

    // Fetch speakers for reassignment dropdown — TA-only feature, skip in
    // readOnly mode (used by recorder/viewer artifact view) since the
    // /sessions/.../speakers endpoint is TA-cookie-only and would 401 for
    // students. Reassignment is also a destructive correction we don't
    // want students doing accidentally during post-session review.
    useEffect(() => {
        if (readOnly) return;
        if (!deviceId || !sessionId) return;
        fetch(`/api/v1/sessions/${sessionId}/devices/${deviceId}/speakers`)
            .then(r => r.ok ? r.json() : [])
            .then(data => setSpeakers(Array.isArray(data) ? data : []))
            .catch(() => {});
    }, [deviceId, sessionId, speakerVersion, readOnly]);

    const handleReassign = (transcriptId, newSpeakerId) => {
        fetch(`/api/v1/transcripts/${transcriptId}/reassign_speaker`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ speaker_id: newSpeakerId }),
        })
            .then(r => r.ok ? r.json() : null)
            .then(updated => {
                if (updated) {
                    // Refresh transcripts to get updated speaker_tag
                    fetchTranscripts();
                }
                setReassigning(null);
            })
            .catch(() => setReassigning(null));
    };

    const formatTime = (seconds) => {
        if (seconds == null) return '';
        const m = Math.floor(seconds / 60);
        const s = Math.floor(seconds % 60);
        return `${m}:${String(s).padStart(2, '0')}`;
    };

    const filtered = searchQuery.trim()
        ? transcripts.filter(t =>
            (t.transcript || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
            (t.speaker_tag || '').toLowerCase().includes(searchQuery.toLowerCase())
          )
        : transcripts;

    // Color by unique speaker_tag
    const speakerIndex = {};
    transcripts.forEach(t => {
        const name = t.speaker_tag || 'Unknown';
        if (!(name in speakerIndex)) speakerIndex[name] = Object.keys(speakerIndex).length;
    });
    const speakerColor = (name) => SPEAKER_COLORS[speakerIndex[name] % SPEAKER_COLORS.length];

    const hasSpeakers = !readOnly && speakers.length > 0;

    return (
        <div className={styles.panel}>
            <div className={styles.panelHeader}>
                <div className={styles.panelTitle}>
                    <span>Transcript</span>
                    {!loading && !error && (
                        <span className={styles.utteranceCount}>{transcripts.length} utterances</span>
                    )}
                </div>
                <input
                    className={styles.searchInput}
                    type="text"
                    placeholder="Search…"
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                />
            </div>

            <div className={styles.panelBody}>
                {loading && <div className={styles.statusMsg}>Loading transcript…</div>}
                {error && <div className={styles.errorMsg}>Failed to load: {error}</div>}
                {!loading && !error && filtered.length === 0 && (
                    <div className={styles.statusMsg}>
                        {searchQuery ? 'No matching utterances' : 'No transcript available'}
                    </div>
                )}
                {!loading && !error && filtered.map((t, i) => {
                    const name = t.speaker_tag || 'Unknown';
                    const color = speakerColor(name);
                    const isReassigning = reassigning === t.id;
                    return (
                        <div key={t.id || i} className={styles.utterance}>
                            <div className={styles.utteranceMeta}>
                                <span className={styles.speakerTag} style={{ borderLeftColor: color, color }}>
                                    {name}
                                </span>
                                {t.start_time != null && (
                                    <span className={styles.timestamp}>{formatTime(t.start_time)}</span>
                                )}
                                {hasSpeakers && !isReassigning && (
                                    <button
                                        className={styles.reassignBtn}
                                        onClick={() => setReassigning(t.id)}
                                        title="Reassign to a different speaker"
                                    >
                                        ↔
                                    </button>
                                )}
                            </div>
                            {isReassigning && (
                                <div className={styles.reassignRow}>
                                    <span className={styles.reassignLabel}>Assign to:</span>
                                    {speakers.map(s => (
                                        <button
                                            key={s.id}
                                            className={`${styles.reassignOption} ${t.speaker_id === s.id ? styles.reassignCurrent : ''}`}
                                            onClick={() => t.speaker_id !== s.id && handleReassign(t.id, s.id)}
                                            disabled={t.speaker_id === s.id}
                                        >
                                            {s.alias}
                                        </button>
                                    ))}
                                    <button
                                        className={styles.reassignCancel}
                                        onClick={() => setReassigning(null)}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            )}
                            <p className={styles.utteranceText}>{t.transcript || ''}</p>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default TranscriptPanel;
